import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, ArrowRight, Store, UtensilsCrossed, QrCode,
  Check, ChefHat, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'restaurant_info', label: 'Restaurant', icon: Store, desc: 'Configurez votre restaurant' },
  { key: 'menu_items', label: 'Menu', icon: UtensilsCrossed, desc: 'Ajoutez vos premiers plats' },
  { key: 'tables_qr', label: 'Tables & QR', icon: QrCode, desc: 'Creez vos tables et QR codes' },
];

export default function OnboardingPage() {
  const { token, restaurant } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state for step 1
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [currency, setCurrency] = useState(restaurant?.currency || 'EUR');

  // Form state for step 2
  const [menuItem, setMenuItem] = useState({ name: '', price: '', category_id: 'mains' });
  const [addedItems, setAddedItems] = useState([]);

  // Form state for step 3
  const [tableCount, setTableCount] = useState(5);

  const CURRENCIES = [
    { code: 'EUR', symbol: '\u20ac', label: 'Euro' },
    { code: 'USD', symbol: '$', label: 'Dollar US' },
    { code: 'THB', symbol: '\u0e3f', label: 'Baht' },
    { code: 'AED', symbol: 'AED', label: 'Dirham' },
    { code: 'GBP', symbol: '\u00a3', label: 'Livre Sterling' },
    { code: 'CHF', symbol: 'CHF', label: 'Franc Suisse' },
    { code: 'MAD', symbol: 'MAD', label: 'Dirham Marocain' },
    { code: 'XOF', symbol: 'CFA', label: 'Franc CFA Ouest' },
    { code: 'XAF', symbol: 'CFA', label: 'Franc CFA Central' },
  ];

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API}/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setStatus(data);
        if (data.completed) {
          const idx = STEPS.length;
          setStep(idx);
        } else {
          const idx = STEPS.findIndex(s => s.key === data.current_step);
          setStep(idx >= 0 ? idx : 0);
        }
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetchStatus();
  }, [token]);

  const saveRestaurantInfo = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/settings/restaurant`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ phone, address, currency }),
      });
      toast.success('Restaurant configure !');
      setStep(1);
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const addMenuItem = async () => {
    if (!menuItem.name || !menuItem.price) { toast.error('Nom et prix requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/menu`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name: menuItem.name,
          price: parseFloat(menuItem.price),
          category_id: menuItem.category_id,
          is_active: true,
        }),
      });
      if (res.ok) {
        const item = await res.json();
        setAddedItems(prev => [...prev, item]);
        setMenuItem({ name: '', price: '', category_id: 'mains' });
        toast.success(`${menuItem.name} ajoute !`);
      }
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const createTables = async () => {
    setSaving(true);
    try {
      for (let i = 1; i <= tableCount; i++) {
        await fetch(`${API}/tables`, {
          method: 'POST', headers,
          body: JSON.stringify({ number: i, seats: 4, zone: 'Main' }),
        });
      }
      toast.success(`${tableCount} tables creees !`);
      // Complete onboarding
      await fetch(`${API}/onboarding/complete`, { method: 'POST', headers });
      navigate('/dashboard');
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="onboarding-page">
      <header className="border-b border-[#1E1E1E]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-3">
          <ChefHat className="w-6 h-6 text-[#D4A853]" />
          <span className="font-bold">Configuration de {restaurant?.name || 'votre restaurant'}</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                i < step ? 'bg-[#2D6A4F]/15 text-[#2D6A4F]' :
                i === step ? 'bg-[#D4A853]/15 text-[#D4A853] border border-[#D4A853]/30' :
                'bg-[#111] text-[#666]'
              }`} data-testid={`onboard-step-${s.key}`}>
                {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-[#2D6A4F]' : 'bg-[#1E1E1E]'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        {step === 0 && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-8" data-testid="step-restaurant">
            <h2 className="text-xl font-bold mb-2">Configurez votre restaurant</h2>
            <p className="text-sm text-[#888] mb-6">Quelques informations de base pour commencer.</p>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="text-xs text-[#888] mb-1 block">Telephone</label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+66 XX XXX XXXX"
                  className="bg-[#0C0C0C] border-[#333] text-white" data-testid="onboard-phone" />
              </div>
              <div>
                <label className="text-xs text-[#888] mb-1 block">Adresse</label>
                <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Adresse du restaurant"
                  className="bg-[#0C0C0C] border-[#333] text-white" data-testid="onboard-address" />
              </div>
              <div>
                <label className="text-xs text-[#888] mb-1 block">Devise</label>
                <select value={currency} onChange={e => setCurrency(e.target.value)}
                  className="w-full h-10 px-3 bg-[#0C0C0C] border border-[#333] rounded-md text-white text-sm" data-testid="onboard-currency">
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.symbol} — {c.label} ({c.code})</option>
                  ))}
                </select>
              </div>
              <Button onClick={saveRestaurantInfo} disabled={saving}
                className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-11 rounded-xl" data-testid="onboard-next-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Suivant <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-8" data-testid="step-menu">
            <h2 className="text-xl font-bold mb-2">Ajoutez vos premiers plats</h2>
            <p className="text-sm text-[#888] mb-6">Vous pourrez en ajouter d'autres plus tard dans l'admin.</p>
            <div className="flex gap-3 mb-4 max-w-lg">
              <Input value={menuItem.name} onChange={e => setMenuItem({...menuItem, name: e.target.value})}
                placeholder="Nom du plat" className="bg-[#0C0C0C] border-[#333] text-white flex-1" data-testid="onboard-item-name" />
              <Input value={menuItem.price} onChange={e => setMenuItem({...menuItem, price: e.target.value})}
                placeholder="Prix" type="number" className="bg-[#0C0C0C] border-[#333] text-white w-24" data-testid="onboard-item-price" />
              <Button onClick={addMenuItem} disabled={saving}
                className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold" data-testid="onboard-add-item">
                +
              </Button>
            </div>
            {addedItems.length > 0 && (
              <div className="mb-4 space-y-1">
                {addedItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#CCC]">
                    <Check className="w-3.5 h-3.5 text-[#2D6A4F]" />
                    {item.name} - {item.price} {currency}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setStep(0)} className="text-[#888]">
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <Button onClick={() => setStep(2)}
                className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-11 rounded-xl" data-testid="onboard-next-2">
                Suivant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-8" data-testid="step-tables">
            <h2 className="text-xl font-bold mb-2">Creez vos tables</h2>
            <p className="text-sm text-[#888] mb-6">Combien de tables a creer ? Les QR codes seront generes automatiquement.</p>
            <div className="flex items-center gap-4 mb-6">
              <label className="text-sm text-[#888]">Nombre de tables :</label>
              <Input type="number" value={tableCount} onChange={e => setTableCount(parseInt(e.target.value) || 1)}
                min={1} max={100} className="w-20 bg-[#0C0C0C] border-[#333] text-white text-center" data-testid="onboard-table-count" />
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-[#888]">
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <Button onClick={createTables} disabled={saving}
                className="bg-[#2D6A4F] hover:bg-[#1B5A3F] text-white font-bold h-11 rounded-xl" data-testid="onboard-finish">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Terminer & Lancer <Check className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        )}

        {step >= STEPS.length && status?.completed && (
          <div className="text-center py-10" data-testid="onboard-complete">
            <div className="w-16 h-16 bg-[#2D6A4F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#2D6A4F]" />
            </div>
            <h2 className="text-xl font-bold mb-2">Configuration terminee !</h2>
            <p className="text-sm text-[#888] mb-6">Votre restaurant est pret.</p>
            <Link to="/dashboard">
              <Button className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-11 px-8 rounded-xl">
                Aller au Dashboard
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
